import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from './ui/Toast';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { formatDate } from '../lib/format';

export interface VatExemptionFields {
  vat_number?: string | null;
  vat_exemption?: 'NONE' | 'INTRA_EU' | 'EXPORT' | null;
  vat_exemption_country?: string | null;
  vat_exemption_note?: string | null;
  vat_exemption_set_at?: string | null;
  vat_exemption_set_by_name?: string | null;
}

/**
 * VAT exemption (migration 134). The standard rate applies to every customer; an exempt one —
 * intra-EU delivery or export — is invoiced at 0 % with the legal wording. Only a manager, an
 * admin or the owner may change it (the server enforces it); everyone else reads it. Details are
 * entered, not checked automatically. Orders already placed keep the regime they were made under.
 */
export default function CustomerVatExemption({
  customerId,
  customer,
  canEdit,
  onChanged,
}: {
  customerId: string;
  customer: VatExemptionFields;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  // Regime labels (relations.vatExemption.labels.*), resolved at render so the language switch applies.
  const label = (r: string) => t(`relations.vatExemption.labels.${r}`);
  const current = customer.vat_exemption ?? 'NONE';
  const [editing, setEditing] = useState(false);
  const [regime, setRegime] = useState(current);
  const [country, setCountry] = useState(customer.vat_exemption_country ?? '');
  const [vatNumber, setVatNumber] = useState(customer.vat_number ?? '');
  const [note, setNote] = useState(customer.vat_exemption_note ?? '');
  const [busy, setBusy] = useState(false);

  const open = () => {
    setRegime(current);
    setCountry(customer.vat_exemption_country ?? '');
    setVatNumber(customer.vat_number ?? '');
    setNote(customer.vat_exemption_note ?? '');
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await apiClient.put(
        `/customers/${customerId}/vat-exemption`,
        {
          exemption: regime,
          country: regime === 'NONE' ? null : country.trim().toUpperCase() || null,
          note: regime === 'NONE' ? null : note.trim() || null,
          ...(regime === 'INTRA_EU' ? { vatNumber: vatNumber.trim() || null } : {}),
        },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({
        tone: 'success',
        message: t('relations.vatExemption.toastSaved', { label: label(regime) }),
      });
      setEditing(false);
      onChanged();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border rounded p-4 bg-card">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="font-semibold text-sm">{t('relations.vatExemption.title')}</h2>
        {canEdit && !editing && (
          <Button variant="outline" onClick={open} className="h-8 text-xs">
            <Pencil className="h-3.5 w-3.5" /> {t('relations.vatExemption.edit')}
          </Button>
        )}
      </div>

      {!editing ? (
        <div className="grid grid-cols-1 gap-x-10 gap-y-1 text-sm md:grid-cols-2">
          <div>
            <span className="text-muted-foreground">{t('relations.vatExemption.regime')}</span>
            <span className={current === 'NONE' ? '' : 'font-semibold'}>{label(current)}</span>
          </div>
          {current !== 'NONE' && (
            <>
              <div>
                <span className="text-muted-foreground">
                  {current === 'INTRA_EU'
                    ? t('relations.vatExemption.memberState')
                    : t('relations.vatExemption.destination')}
                </span>
                {customer.vat_exemption_country ?? '—'}
              </div>
              {current === 'INTRA_EU' && (
                <div>
                  <span className="text-muted-foreground">
                    {t('relations.vatExemption.euVatNumber')}
                  </span>
                  <span className="font-mono">{customer.vat_number ?? '—'}</span>
                </div>
              )}
              {customer.vat_exemption_note && (
                <div>
                  <span className="text-muted-foreground">
                    {t('relations.vatExemption.details')}
                  </span>
                  {customer.vat_exemption_note}
                </div>
              )}
            </>
          )}
          {customer.vat_exemption_set_at && (
            <div className="text-xs text-muted-foreground md:col-span-2">
              {t('relations.vatExemption.setBy', {
                name: customer.vat_exemption_set_by_name ?? '—',
                date: formatDate(customer.vat_exemption_set_at),
              })}
            </div>
          )}
          {!canEdit && (
            <div className="text-xs text-muted-foreground md:col-span-2">
              {t('relations.vatExemption.readOnly')}
            </div>
          )}
        </div>
      ) : (
        <div className="max-w-xl space-y-3 text-sm">
          <fieldset className="space-y-1">
            {(['NONE', 'INTRA_EU', 'EXPORT'] as const).map((r) => (
              <label key={r} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="vat-regime"
                  checked={regime === r}
                  onChange={() => setRegime(r)}
                />
                {label(r)}
              </label>
            ))}
          </fieldset>
          {regime !== 'NONE' && (
            <>
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">
                  {regime === 'INTRA_EU'
                    ? t('relations.vatExemption.memberStateInput')
                    : t('relations.vatExemption.destinationInput')}
                </span>
                <Input
                  className="w-24 uppercase"
                  maxLength={2}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </label>
              {regime === 'INTRA_EU' && (
                <label className="block">
                  <span className="mb-1 block text-xs text-muted-foreground">
                    {t('relations.vatExemption.euVatNumberInput')}
                  </span>
                  <Input
                    className="w-64 font-mono"
                    value={vatNumber}
                    onChange={(e) => setVatNumber(e.target.value)}
                  />
                </label>
              )}
              <label className="block">
                <span className="mb-1 block text-xs text-muted-foreground">
                  {t('relations.vatExemption.detailsInput')}
                </span>
                <Input value={note} onChange={(e) => setNote(e.target.value)} />
              </label>
            </>
          )}
          <p className="text-xs text-muted-foreground">
            {t('relations.vatExemption.appliesToNew')}
          </p>
          <div className="flex gap-2">
            <Button onClick={() => void save()} disabled={busy}>
              {t('relations.vatExemption.save')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>
              {t('relations.vatExemption.cancel')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
